import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Search, Heart, ShoppingBag } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import SearchOverlay from './SearchOverlay';
import CartSheet from './CartSheet';
import ProfileMenu from './ProfileMenu';
import '../pages/home.css';
import { track } from '../lib/tracking';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * MaisonNav — one nav for the entire storefront.
 *  • Starts fully transparent over the page on every route.
 *  • Turns into a floating theme-coloured bar as soon as the user scrolls.
 *  • White text only on the home hero (dark background); charcoal everywhere else.
 *  • "Collections" opens a floating menu of CATEGORIES (icon + name).
 */
export default function MaisonNav() {
  const { pathname } = useLocation();
  const isHome = pathname === '/';
  // Keep POS and barcode-sheet as fullscreen work surfaces (no nav). Admin now
  // shows the storefront nav on top per user request so they can always jump
  // back to the site or reach the account menu.
  const hidden = pathname.startsWith('/pos') || pathname.startsWith('/barcode-sheet');

  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [colsOpen, setColsOpen] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showCart, setShowCart] = useState(false);
  const [categories, setCategories] = useState([]);
  const [logo, setLogo] = useState('');
  const colsCloseTimer = useRef(null);
  const navRef = useRef(null);

  const { user } = useAuth();
  const { cart } = useCart();
  // `cart` is an array of items, not { items: [...] } — early code assumed the
  // latter and cartCount always came out 0 in the nav / mobile menu.
  const cartCount = (Array.isArray(cart) ? cart : []).reduce((n, i) => n + (i.quantity || 0), 0);

  useEffect(() => {
    setScrolled(typeof window !== 'undefined' && window.scrollY > 24);
    setColsOpen(false); setMenuOpen(false);
  }, [pathname]);

  // Publish the real nav height as --nav-h so fixed layouts (Admin, POS, pages)
  // never get covered by the logo bar, regardless of logo size or scroll state.
  useEffect(() => {
    const root = document.documentElement;
    if (hidden) { root.style.setProperty('--nav-h', '0px'); return; }
    const el = navRef.current;
    if (!el) return;
    const set = () => root.style.setProperty('--nav-h', `${Math.ceil(el.offsetHeight)}px`);
    set();
    const ro = new ResizeObserver(set);
    ro.observe(el);
    window.addEventListener('resize', set);
    return () => { ro.disconnect(); window.removeEventListener('resize', set); };
  }, [hidden, scrolled, logo, pathname]);

  useEffect(() => {
    if (hidden) return;
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [hidden, pathname]);

  // One-time: fetch categories + logo
  useEffect(() => {
    if (hidden) return;
    axios.get(`${API}/api/categories`).then(({ data }) => setCategories(Array.isArray(data) ? data : [])).catch(() => {});
    axios.get(`${API}/api/cms/blocks`).then(({ data }) => {
      const block = (data || []).find(b => b?.block_id === 'home:media:logo');
      if (block?.content?.url) setLogo(block.content.url);
    }).catch(() => {});
  }, [hidden]);

  const catsForNav = useMemo(() => (
    (categories || []).map(c => ({ name: c.name, image: c.image || '' }))
  ), [categories]);

  if (hidden) return null;

  // Always render the nav in charcoal / ivory (never the transparent-white hero variant).
  // Per user request the logo + menu should be black across every route.
  const dark = false;

  return (
    <div className="diosa-home" style={{ minHeight: 0, background: 'transparent', overflow: 'visible' }}>
      <nav ref={navRef} className={`dnav ${scrolled ? 'scrolled' : ''}`} data-testid="site-nav">
        <div className="nav-side left">
          <div className="cols-wrap"
               onMouseEnter={() => { clearTimeout(colsCloseTimer.current); setColsOpen(true); }}
               onMouseLeave={() => { colsCloseTimer.current = setTimeout(() => setColsOpen(false), 140); }}>
            <button className={`nav-link ${dark ? 'on-dark' : ''}`} data-testid="nav-collections"
                    onClick={() => setColsOpen(v => !v)}>
              Collections
            </button>
          </div>
          <Link to="/atelier" className={`nav-link ${dark ? 'on-dark' : ''}`} data-testid="nav-atelier">The Atelier</Link>
          <Link to="/court" className={`nav-link ${dark ? 'on-dark' : ''}`} data-testid="nav-court">The Court</Link>
        </div>
        <Link to="/" className={`dlogo ${dark ? 'on-dark' : ''}`} data-testid="site-logo">
          {logo
            ? <img src={logo} alt="Diosa Forever" />
            : (
              <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1 }}>
                <span style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontStyle: 'italic', fontWeight: 400, fontSize: 39, letterSpacing: '0.06em' }}>Diosa</span>
                <span style={{ fontFamily: "'Inter', system-ui, sans-serif", fontSize: 11, letterSpacing: '0.32em', textTransform: 'uppercase', marginTop: 5, opacity: .7 }}>Forever</span>
              </span>
            )}
        </Link>
        <div className="nav-side right">
          <button className={`nav-icon ${dark ? 'on-dark' : ''}`} onClick={() => setShowSearch(true)} aria-label="Search" data-testid="nav-search-btn">
            <Search size={17} strokeWidth={1.5} />
          </button>
          <Link to="/wishlist" className={`nav-icon mobile-hide ${dark ? 'on-dark' : ''}`} aria-label="Wishlist" data-testid="nav-wishlist-btn">
            <Heart size={17} strokeWidth={1.5} />
          </Link>
          <ProfileMenu dark={dark} />
          <button className={`nav-icon nav-cart-wrap ${dark ? 'on-dark' : ''}`} onClick={() => { track('cart_view'); setShowCart(true); }} aria-label="Cart" data-testid="nav-cart-btn">
            <ShoppingBag size={17} strokeWidth={1.5} />
            {cartCount > 0 && <span className="cart-count">{cartCount}</span>}
          </button>
        </div>
        <button className={`nav-toggle ${pathname.startsWith('/admin') ? 'nav-toggle-hide-mobile' : ''}`} style={{ color: dark ? '#fff' : 'var(--charcoal)' }} aria-label="Menu"
                onClick={() => setMenuOpen(v => !v)} data-testid="nav-toggle">
          <span /><span />
        </button>
      </nav>

      {/* Collections flyout — CATEGORIES as icon + name */}
      <div className={`cols-panel ${colsOpen ? 'open' : ''}`}
           onMouseEnter={() => { clearTimeout(colsCloseTimer.current); setColsOpen(true); }}
           onMouseLeave={() => { colsCloseTimer.current = setTimeout(() => setColsOpen(false), 140); }}
           data-testid="collections-dropdown">
        <p className="cols-eyebrow">Shop by Category</p>
        <div className="cats-flyout">
          {catsForNav.map(c => (
            <Link key={c.name} to={`/products?category=${encodeURIComponent(c.name)}`} className="cats-flyout-item"
                  onClick={() => setColsOpen(false)}
                  data-testid={`cols-cat-${c.name.toLowerCase().replace(/\s+/g, '-')}`}>
              <span className="cats-flyout-icon">
                {c.image
                  ? <img src={c.image} alt="" loading="lazy" />
                  : <span className="cats-flyout-initial">{c.name.charAt(0)}</span>}
              </span>
              <span className="cats-flyout-name">{c.name}</span>
            </Link>
          ))}
          {catsForNav.length === 0 && <p style={{ textAlign: 'center', opacity: .5, fontSize: 13, fontStyle: 'italic' }}>No categories yet</p>}
        </div>
        <div className="cols-footer">
          <Link to="/products" data-testid="cols-link-collection" onClick={() => setColsOpen(false)}>The Shop · View Everything →</Link>
        </div>
      </div>

      <div className={`mobile-menu ${menuOpen ? 'open' : ''}`} data-testid="mobile-menu">
        <div className="m-group">
          <p className="m-eyebrow">Shop by Category</p>
          {catsForNav.map(c => (
            <Link key={c.name} to={`/products?category=${encodeURIComponent(c.name)}`} className="m-link" onClick={() => setMenuOpen(false)} data-testid={`mm-cat-${c.name.toLowerCase().replace(/\s+/g, '-')}`}>{c.name}</Link>
          ))}
          {catsForNav.length === 0 && <p style={{ fontSize: 12, opacity: .55, fontStyle: 'italic' }}>No categories yet</p>}
          <Link to="/products" className="m-link small" onClick={() => setMenuOpen(false)} style={{ color: 'var(--gold)', marginTop: 6 }} data-testid="mm-shop-all">View Everything →</Link>
        </div>
        <div className="m-group">
          <p className="m-eyebrow">The House</p>
          <Link to="/atelier" className="m-link" onClick={() => setMenuOpen(false)} data-testid="mm-atelier">The Atelier</Link>
          <Link to="/court" className="m-link" onClick={() => setMenuOpen(false)} data-testid="mm-court">The Court</Link>
        </div>
        <div className="m-actions">
          <button onClick={() => { setMenuOpen(false); setShowSearch(true); }} data-testid="mm-search">Search</button>
          <Link to="/wishlist" onClick={() => setMenuOpen(false)} data-testid="mm-wishlist">Wishlist</Link>
          <Link to={user ? '/account' : '/login'} onClick={() => setMenuOpen(false)} data-testid="mm-account">{user ? 'Account' : 'Login'}</Link>
          <button onClick={() => { setMenuOpen(false); setShowCart(true); }} className="primary" data-testid="mm-cart">Cart{cartCount ? ` · ${cartCount}` : ''}</button>
        </div>
      </div>

      <SearchOverlay isOpen={showSearch} onClose={() => setShowSearch(false)} />
      <CartSheet isOpen={showCart} onClose={() => setShowCart(false)} />
    </div>
  );
}
